import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useEvent } from '../hooks/useEvent';
import { useAuth } from '../hooks/useAuth';
import alertsService, { type EventAlert, notifyBeforeToMs, canRoleViewAlert } from '../api/alertsService';
import { isEventActive as checkEventActive } from '../utils/eventSource';
import { useAlertSSE, isSSEConfigured } from '../hooks/useAlertSSE';
import { useCollectionFreshness } from '../hooks/useCollectionFreshness';

/**
 * State stored in localStorage for alert notifications
 */
interface AlertNotificationStorageState {
  dismissedAlertIds: string[];      // Alerts user has dismissed (won't show popup again)
  acknowledgedTriggers: string[];   // Trigger keys user has acknowledged (e.g., "alertId:immediate")
  lastSeenTimestamp: string;        // For tracking "new" alerts
  popupAlertsMuted: boolean;        // When true, popup toasts are disabled (reduce noise during session)
  compactAlerts: boolean;           // When true, toast notifications use a minimal single-line style
}

/**
 * Context state and methods
 */
interface AlertNotificationContextType {
  // State
  alerts: EventAlert[];
  activeAlerts: EventAlert[];       // Filtered alerts that should be shown
  unreadCount: number;              // Number of alerts not yet seen
  loading: boolean;
  
  // Methods
  dismissAlert: (alertId: string) => void;
  acknowledgeTrigger: (triggerKey: string) => void;  // Changed to accept trigger key
  markAllAsSeen: () => void;
  refreshAlerts: () => Promise<void>;
  
  // Helpers
  isAlertDismissed: (alertId: string) => boolean;
  isTriggerAcknowledged: (triggerKey: string) => boolean;  // Changed to check trigger key
  getNewAlerts: () => EventAlert[];
  popupAlertsMuted: boolean;
  setPopupAlertsMuted: (muted: boolean) => void;
  compactAlerts: boolean;
  setCompactAlerts: (compact: boolean) => void;
}

const AlertNotificationContext = createContext<AlertNotificationContextType | undefined>(undefined);

const STORAGE_KEY = 'alert_notification_state';

/**
 * Get stored state from localStorage
 */
function getStoredState(): AlertNotificationStorageState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Migration: convert old acknowledgedAlertIds to acknowledgedTriggers
      if (parsed.acknowledgedAlertIds && !parsed.acknowledgedTriggers) {
        parsed.acknowledgedTriggers = [];
      }
      return {
        dismissedAlertIds: parsed.dismissedAlertIds || [],
        acknowledgedTriggers: parsed.acknowledgedTriggers || [],
        lastSeenTimestamp: parsed.lastSeenTimestamp || new Date().toISOString(),
        popupAlertsMuted: parsed.popupAlertsMuted ?? false,
        compactAlerts: parsed.compactAlerts ?? false,
      };
    }
  } catch (e) {
    console.error('Failed to parse alert notification state:', e);
  }
  return {
    dismissedAlertIds: [],
    acknowledgedTriggers: [],
    lastSeenTimestamp: new Date().toISOString(),
    popupAlertsMuted: false,
    compactAlerts: false,
  };
}

/**
 * Save state to localStorage
 * Limits array sizes to prevent unbounded growth
 */
function saveStoredState(state: AlertNotificationStorageState): void {
  try {
    // Limit arrays to prevent memory issues over time
    const MAX_DISMISSED = 500;
    const MAX_ACKNOWLEDGED = 1000;
    
    const limitedState = {
      ...state,
      dismissedAlertIds: state.dismissedAlertIds.slice(-MAX_DISMISSED),
      acknowledgedTriggers: state.acknowledgedTriggers.slice(-MAX_ACKNOWLEDGED),
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedState));
  } catch (e) {
    console.error('Failed to save alert notification state:', e);
  }
}

/**
 * Round a date to the start of the current minute (seconds = 0, milliseconds = 0)
 * This ensures consistent comparisons since polling happens at round minutes
 */
function roundToMinute(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), 0, 0);
}

/**
 * Check if an alert should be shown in real-time notifications
 * Considers: show_immediately, notify_before schedule, and start_time/end_time
 */
function shouldShowAlert(alert: EventAlert, now: Date): boolean {
  // Round now to the start of the current minute for consistent comparisons
  const nowRounded = roundToMinute(now);
  
  // Don't show deleted alerts
  if (alert.is_deleted) {
    return false;
  }
  
  // Check if expired (end_time passed)
  if (alert.end_time) {
    const endTime = new Date(alert.end_time);
    if (endTime < nowRounded) {
      return false;
    }
  }
  
  // Show if show_immediately is true
  if (alert.show_immediately) {
    return true;
  }
  
  const startTime = new Date(alert.start_time);
  
  // Check if any notify_before time has passed
  if (alert.notify_before && alert.notify_before.length > 0) {
    for (const entry of alert.notify_before) {
      const notifyTime = new Date(startTime.getTime() - notifyBeforeToMs(entry));
      if (notifyTime <= nowRounded) return true;
    }
  }
  
  // Otherwise, show if start_time is in the past or now
  return startTime <= nowRounded;
}

export const AlertNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selectedEvent } = useEvent();
  const { isAuthenticated, authState } = useAuth();
  const userRole = authState.user?.role || '';
  const [alerts, setAlerts] = useState<EventAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [storedState, setStoredState] = useState<AlertNotificationStorageState>(getStoredState);
  
  // Track previous alerts to detect new ones
  const previousAlertIdsRef = useRef<Set<string>>(new Set());
  const newAlertsRef = useRef<EventAlert[]>([]);
  
  // Track lastSeenTimestamp as a ref to avoid callback recreation
  const lastSeenTimestampRef = useRef<string>(storedState.lastSeenTimestamp);
  
  // Check if this is a non-legacy event
  const isNonLegacyEvent = selectedEvent && !selectedEvent.legacy;
  
  // Check if the event is currently active (ongoing)
  // Uses circuit timezone for consistent behavior across the app
  const isEventActive = checkEventActive(selectedEvent);

  // Only poll for active, non-legacy events
  const shouldPollAlerts = isNonLegacyEvent && isEventActive;

  // Metadata polling replaces the 10-minute fallback interval
  const metadataTimestamps = useCollectionFreshness(['event_alerts'], {
    interval: 30_000,
    enabled: !!shouldPollAlerts,
  });
  const lastAlertMetaRef = useRef<string>('');

  /**
   * Fetch alerts from the API
   */
  const fetchAlerts = useCallback(async () => {
    // Don't make API calls if user is not authenticated
    if (!isAuthenticated) {
      return;
    }

    if (!selectedEvent || selectedEvent.legacy) {
      setAlerts([]);
      return;
    }

    setLoading(true);
    try {
      const response = await alertsService.getEventAlerts(
        selectedEvent.season_year,
        selectedEvent.circuit_id,
        selectedEvent.start_date,
        selectedEvent.demo ?? false
      );

      if (response.success && response.data) {
        const fetchedAlerts = response.data;
        
        // Detect new alerts (use ref to avoid callback recreation)
        const currentIds = new Set(fetchedAlerts.map(a => a.id));
        const newAlerts = fetchedAlerts.filter(
          a => !previousAlertIdsRef.current.has(a.id) && 
               new Date(a.created_at) > new Date(lastSeenTimestampRef.current)
        );
        
        if (newAlerts.length > 0) {
          newAlertsRef.current = newAlerts;
        }
        
        previousAlertIdsRef.current = currentIds;
        setAlerts(fetchedAlerts);
      }
    } catch (error) {
      console.error('Failed to fetch alerts for notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent, isAuthenticated]);

  /**
   * Refresh alerts manually
   */
  const refreshAlerts = useCallback(async () => {
    await fetchAlerts();
  }, [fetchAlerts]);

  /**
   * Filter alerts that should be shown
   * Note: We don't filter out acknowledged alerts here - let the toast manager handle trigger-level acknowledgment
   */
  const activeAlerts = React.useMemo(() => {
    const now = new Date();
    return alerts.filter(alert => {
      if (!shouldShowAlert(alert, now)) return false;
      // Filter by user role - only show alerts the user's role can view
      if (!canRoleViewAlert(alert, userRole)) return false;
      // Only filter by dismissed status - acknowledgment is handled per-trigger by toast manager
      return !storedState.dismissedAlertIds.includes(alert.id);
    });
  }, [alerts, storedState.dismissedAlertIds, userRole]);

  /**
   * Count unread alerts (created after lastSeenTimestamp)
   */
  const unreadCount = React.useMemo(() => {
    const lastSeen = new Date(storedState.lastSeenTimestamp);
    return activeAlerts.filter(a => new Date(a.created_at) > lastSeen).length;
  }, [activeAlerts, storedState.lastSeenTimestamp]);

  /**
   * Get new alerts since last fetch (for toast notifications)
   */
  const getNewAlerts = useCallback(() => {
    const newAlerts = newAlertsRef.current;
    newAlertsRef.current = []; // Clear after reading
    return newAlerts;
  }, []);

  /**
   * Dismiss an alert (won't show popup again, but stays in list)
   */
  const dismissAlert = useCallback((alertId: string) => {
    setStoredState(prev => {
      const newState = {
        ...prev,
        dismissedAlertIds: [...prev.dismissedAlertIds, alertId],
      };
      saveStoredState(newState);
      return newState;
    });
  }, []);

  /**
   * Acknowledge a specific trigger (for requires_acknowledgment alerts)
   * triggerKey format: "alertId:triggerType" (e.g., "abc123:immediate", "abc123:notify:30:minutes")
   */
  const acknowledgeTrigger = useCallback((triggerKey: string) => {
    setStoredState(prev => {
      const newState = {
        ...prev,
        acknowledgedTriggers: [...prev.acknowledgedTriggers, triggerKey],
      };
      saveStoredState(newState);
      return newState;
    });
  }, []);

  /**
   * Mark all current alerts as seen
   */
  const markAllAsSeen = useCallback(() => {
    const now = new Date().toISOString();
    lastSeenTimestampRef.current = now; // Update ref immediately
    setStoredState(prev => {
      const newState = {
        ...prev,
        lastSeenTimestamp: now,
      };
      saveStoredState(newState);
      return newState;
    });
  }, []);

  /**
   * Check if alert is dismissed
   */
  const isAlertDismissed = useCallback((alertId: string) => {
    return storedState.dismissedAlertIds.includes(alertId);
  }, [storedState.dismissedAlertIds]);

  /**
   * Check if a specific trigger is acknowledged
   */
  const isTriggerAcknowledged = useCallback((triggerKey: string) => {
    return storedState.acknowledgedTriggers.includes(triggerKey);
  }, [storedState.acknowledgedTriggers]);

  /**
   * Toggle popup alerts (mute/unmute) - persisted to localStorage
   */
  const setPopupAlertsMuted = useCallback((muted: boolean) => {
    setStoredState(prev => {
      const newState = { ...prev, popupAlertsMuted: muted };
      saveStoredState(newState);
      return newState;
    });
  }, []);

  /**
   * Toggle compact alert style - persisted to localStorage
   */
  const setCompactAlerts = useCallback((compact: boolean) => {
    setStoredState(prev => {
      const newState = { ...prev, compactAlerts: compact };
      saveStoredState(newState);
      return newState;
    });
  }, []);

  // Connect to SSE for real-time notifications (when configured)
  // SSE will trigger immediate refreshes when alerts with show_immediately=true are created/updated
  const { isConnected: sseConnected } = useAlertSSE({
    seasonYear: selectedEvent?.season_year || 0,
    circuitId: selectedEvent?.circuit_id || '',
    startDate: selectedEvent?.start_date || '',
    onRefreshNeeded: fetchAlerts,
    enabled: !!shouldPollAlerts && isSSEConfigured() && isAuthenticated,
  });

  // Log SSE connection status in development
  useEffect(() => {
    if (isSSEConfigured() && shouldPollAlerts && isAuthenticated) {
      console.log(`[AlertContext] SSE ${sseConnected ? 'connected' : 'disconnected'}, metadata polling: 30s`);
    }
  }, [sseConnected, shouldPollAlerts, isAuthenticated]);

  // Fetch alerts when event changes (for active, non-legacy events)
  useEffect(() => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      setAlerts([]);
      return;
    }

    if (shouldPollAlerts) {
      fetchAlerts();
    } else if (isNonLegacyEvent && !isEventActive) {
      // For past events, fetch once but don't poll (for viewing history)
      fetchAlerts();
    } else {
      setAlerts([]);
      previousAlertIdsRef.current = new Set();
    }
  }, [isNonLegacyEvent, isEventActive, shouldPollAlerts, fetchAlerts, isAuthenticated]);

  // Metadata-driven refresh: replace 10-minute interval with 30-second metadata polling.
  // SSE remains the primary real-time mechanism; metadata polling is the backup.
  useEffect(() => {
    const latest = metadataTimestamps.event_alerts;
    if (latest && latest > lastAlertMetaRef.current) {
      lastAlertMetaRef.current = latest;
      fetchAlerts();
    }
  }, [metadataTimestamps, fetchAlerts]);

  const value: AlertNotificationContextType = {
    alerts,
    activeAlerts,
    unreadCount,
    loading,
    dismissAlert,
    acknowledgeTrigger,
    markAllAsSeen,
    refreshAlerts,
    isAlertDismissed,
    isTriggerAcknowledged,
    getNewAlerts,
    popupAlertsMuted: storedState.popupAlertsMuted,
    setPopupAlertsMuted,
    compactAlerts: storedState.compactAlerts,
    setCompactAlerts,
  };

  return (
    <AlertNotificationContext.Provider value={value}>
      {children}
    </AlertNotificationContext.Provider>
  );
};

export { AlertNotificationContext };
